import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import {
  useMyMaintenanceInfaq,
  useWithdrawMaintenanceInfaq,
  useSubmitClosingReport,
} from '../../hooks/useMaintenanceInfaq';
import { maintenanceInfaqService } from '../../services/maintenance-infaq.service';
import toast from 'react-hot-toast';

const STATUS_COLORS: Record<string, string> = {
  PENDING_REVIEW: 'bg-amber-100 text-amber-800',
  OPEN: 'bg-green-100 text-green-800',
  CLOSED: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-emerald-100 text-emerald-800',
  REJECTED: 'bg-red-100 text-red-800',
  WITHDRAWN: 'bg-neutral-100 text-neutral-700',
};

export default function MaintenanceInfaqDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: campaign, isLoading } = useMyMaintenanceInfaq(id);
  const withdrawMutation = useWithdrawMaintenanceInfaq();
  const closingMutation = useSubmitClosingReport();

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-neutral-500">Loading…</div>
    );
  }
  if (!campaign) {
    return (
      <div className="p-6 text-sm text-neutral-500">
        Campaign not found.
      </div>
    );
  }

  const raised = Number(campaign.raisedAmount || 0);
  const target = Number(campaign.targetAmount || 0);
  const progressPct = target > 0 ? Math.min(100, Math.round((raised / target) * 100)) : 0;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <button
        onClick={() => navigate('/provider-dashboard/infaq')}
        className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        {t('common.back', { defaultValue: 'Back' })}
      </button>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-neutral-900">{campaign.title}</h1>
            <span
              className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                STATUS_COLORS[campaign.status] ?? 'bg-neutral-100'
              }`}
            >
              {campaign.status.replace(/_/g, ' ').toLowerCase()}
            </span>
          </div>
          <p className="mt-1 text-sm text-neutral-600">{campaign.reason}</p>
        </div>
        {campaign.status === 'PENDING_REVIEW' && (
          <button
            onClick={async () => {
              await withdrawMutation.mutateAsync(campaign.id);
              navigate('/provider-dashboard/infaq');
            }}
            className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50"
          >
            Withdraw
          </button>
        )}
      </header>

      {campaign.rejectionReason && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <strong>Rejected:</strong> {campaign.rejectionReason}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-3">
        <Card title="Amount">
          <div className="text-2xl font-semibold tabular-nums">
            RM {raised.toFixed(2)}
          </div>
          <div className="text-xs text-neutral-500">
            of RM {target.toFixed(2)} target
          </div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
            <div
              className="h-full bg-primary-600"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="mt-1 text-xs text-neutral-500">{progressPct}% funded</div>
        </Card>

        <Card title="Timeline">
          <Row label="Created" value={fmt(campaign.createdAt)} />
          {campaign.approvedAt && (
            <Row label="Approved" value={fmt(campaign.approvedAt)} />
          )}
          {campaign.maxEndDate && (
            <Row label="Closes by" value={fmt(campaign.maxEndDate)} />
          )}
          {campaign.closedAt && (
            <Row label="Closed" value={fmt(campaign.closedAt)} />
          )}
          {campaign.completedAt && (
            <Row label="Completed" value={fmt(campaign.completedAt)} />
          )}
        </Card>

        <Card title="Invoice tier">
          <div className="space-y-1 text-sm">
            <div>
              {campaign.requiresInvoice
                ? 'Required (quote + actual receipts)'
                : 'Not required'}
            </div>
            {campaign.tireChange && (
              <div className="text-xs text-amber-700">
                Tire change / major part flagged
              </div>
            )}
          </div>
          {campaign.submissionInvoiceUrls &&
            Array.isArray(campaign.submissionInvoiceUrls) &&
            campaign.submissionInvoiceUrls.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs">
                {(campaign.submissionInvoiceUrls as string[]).map((u, i) => (
                  <li key={i}>
                    <a
                      href={u}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-primary-700 hover:underline"
                    >
                      <PaperClipIcon className="h-3 w-3" /> Invoice {i + 1}
                    </a>
                  </li>
                ))}
              </ul>
            )}
        </Card>
      </div>

      <Card title="Description">
        <p className="whitespace-pre-wrap text-sm text-neutral-700">
          {campaign.description}
        </p>
        {campaign.damagePhotoUrls &&
          Array.isArray(campaign.damagePhotoUrls) &&
          (campaign.damagePhotoUrls as string[]).length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {(campaign.damagePhotoUrls as string[]).map((u, i) => (
                <a
                  key={i}
                  href={u}
                  target="_blank"
                  rel="noreferrer"
                  className="block overflow-hidden rounded-md border border-neutral-200"
                >
                  <img src={u} alt={`damage-${i}`} className="h-24 w-full object-cover" />
                </a>
              ))}
            </div>
          )}
      </Card>

      {campaign.status === 'CLOSED' && (
        <ClosingReportSection
          campaign={campaign}
          submitting={closingMutation.isPending}
          onSubmit={async (payload) => {
            await closingMutation.mutateAsync({ id: campaign.id, payload });
          }}
          rejectionReason={campaign.closingRejectionReason ?? undefined}
        />
      )}

      {campaign.status === 'COMPLETED' && (
        <Card title="Disbursement">
          <Row
            label="Disbursed"
            value={`RM ${Number(campaign.disbursedAmount ?? 0).toFixed(2)}`}
          />
          {campaign.disbursedAt && (
            <Row label="On" value={fmt(campaign.disbursedAt)} />
          )}
          <p className="mt-2 text-xs text-neutral-500">
            Funds were credited to your wallet.
          </p>
        </Card>
      )}
    </div>
  );
}

function ClosingReportSection({
  campaign,
  submitting,
  onSubmit,
  rejectionReason,
}: {
  campaign: any;
  submitting: boolean;
  onSubmit: (payload: {
    actualCost: number;
    workDescription: string;
    receiptUrls: string[];
    notes?: string;
  }) => Promise<void>;
  rejectionReason?: string;
}) {
  const [actualCost, setActualCost] = useState<number>(0);
  const [workDescription, setWorkDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const priorReport = campaign.closingReport;
  const requiresReceipts = campaign.requiresInvoice;

  const submit = async () => {
    if (!workDescription.trim()) {
      toast.error('Please describe the work done');
      return;
    }
    if (requiresReceipts && files.length === 0) {
      toast.error('Actual receipts are required for invoice-tier campaigns');
      return;
    }
    try {
      setUploading(true);
      const receiptUrls: string[] = [];
      for (const f of files) {
        receiptUrls.push(await maintenanceInfaqService.uploadFile(f));
      }
      setUploading(false);
      await onSubmit({
        actualCost: Number(actualCost) || 0,
        workDescription: workDescription.trim(),
        receiptUrls,
        notes: notes.trim() || undefined,
      });
    } catch {
      setUploading(false);
    }
  };

  return (
    <Card title="Closing report">
      {rejectionReason && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <strong>Report rejected:</strong> {rejectionReason}. Please re-submit.
        </div>
      )}
      {priorReport && (
        <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
          A previous report was submitted on{' '}
          {new Date(priorReport.submittedAt).toLocaleString()}.
        </div>
      )}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-neutral-800">
            Actual cost (RM)
          </label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={actualCost || ''}
            onChange={(e) => setActualCost(parseFloat(e.target.value) || 0)}
            className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-800">
            Work description *
          </label>
          <textarea
            rows={4}
            value={workDescription}
            onChange={(e) => setWorkDescription(e.target.value)}
            className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-800">
            Receipts {requiresReceipts ? '*' : '(optional)'}
          </label>
          <div className="mt-1 flex items-center gap-2">
            <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50">
              <PaperClipIcon className="h-4 w-4" /> Choose files
              <input
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  const picked = Array.from(e.target.files ?? []);
                  setFiles([...files, ...picked]);
                  e.target.value = '';
                }}
              />
            </label>
            <span className="text-xs text-neutral-500">
              {files.length} file(s) selected
            </span>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-800">Notes</label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
          />
        </div>
        <button
          onClick={submit}
          disabled={submitting || uploading}
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting || uploading ? 'Submitting…' : 'Submit closing report'}
        </button>
      </div>
    </Card>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-neutral-200 bg-white p-4">
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-500">
        {title}
      </h2>
      <div className="space-y-1 text-sm text-neutral-800">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-neutral-500">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

function fmt(d?: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return d;
  }
}
